import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { UserProvider } from './providers/user-provider';
import { ColorSchemeProvider, useColorScheme } from './providers/color-scheme-provider';
import { LogoutProvider } from './providers/logout-context';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import NotificationProvider from './providers/toaster-provider';
import { SettingsProvider } from './providers/settings-provider';
import { BankProvider } from './providers/bank-provider';
import Sidebar, { SidebarContext, SIDEBAR_WIDTH, SIDEBAR_WIDTH_COMPRESSED } from './components/sidebar';
import theme from './util/theme/theme';
import AuthGuard from './components/auth-guard';
import PermissionGuard from './components/permission-guard';
import { LocationProvider } from './providers/location-provider';
import { SidebarMobileProvider } from './providers/sidebar-mobile-context';
import ErrorBoundary from './components/error-boundary';
import ProvisionAgingWatcher from './components/provision-aging-watcher';
import IdleTimeout from './components/idle-timeout';
import PlanRequiredGate from './components/plan-required-gate';

// Client Pages
import Provisions from './pages/provisions';
import CategoriesPage from './pages/categories';
import TaxDefinitions from './pages/tax';
import Staff from './pages/staff';
import Login from './pages/login';
import Signup from './pages/signup';
import ResetPassword from './pages/reset-password';
import AccountSettings from './pages/company-settings';
import ProfileSettings from './pages/profile-settings';
import InvoiceSettings from './pages/invoice-settings';
import ProvisionSettings from './pages/provision-settings';
import BankAccounts from './pages/bank-accounts';
import Security from './pages/security-settings';
import Contributions from './pages/contributions';
import Invoice from './pages/invoices';
import Client from './pages/clients';
import Documents from './pages/documents';
import Imports from './pages/imports';
import LinkedImports from './pages/linked-imports';
import TransactionsVirtual from './pages/transactions-virtual';
import ColorSchemeSettings from './pages/color-scheme-settings';
import ProfitLoss from './pages/profit-loss';
import Forecast from './pages/forecast';
import Assumptions from './pages/assumptions';
import BudgetActual from './pages/budget-actual';
import GST from './pages/geo-locked/gst';
import Integrations from './pages/integrations';
import { PermissionsProvider } from './providers/permissions-provider';
import { FeaturesProvider } from './providers/features-provider';

// Social Auth Callbacks
import GoogleCallback from './pages/auth/google-callback';
import MicrosoftCallback from './pages/auth/microsoft-callback';

// Admin Pages
import AdminLogin from './pages/admin/login';
import Company from './pages/admin/company';
import Bank from './pages/admin/bank';
import Country from './pages/admin/country';
import CategoryProviderPage from './pages/admin/category-provider';
import InvoiceProviderPage from './pages/admin/invoice-provider';
import AdminProfile from './pages/admin/profile';
import ActivityLogs from './pages/admin/activity-logs';
import AdminImpersonate from './pages/admin/impersonate';
import FeatureFlagsAdmin from './pages/admin/feature-flags';
import SubscriptionTiersAdmin from './pages/admin/subscription-tiers';
import SubscriptionAddonsAdmin from './pages/admin/subscription-addons';
import SubscriptionReimbursementsAdmin from './pages/admin/subscription-reimbursements';
import StripeSettingsAdmin from './pages/admin/stripe-settings';

// Other Pages
import DocumentViewer from './pages/document-viewer';
import Lab from './pages/lab';
import DevOptions from './pages/dev-options';
import TermsAndConditions from './pages/terms-and-conditions';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Nested MUI ThemeProviders override the outer one for their subtree - this
// lets the outer <ThemeProvider theme={theme}> (below) keep serving a sane
// default for pages rendered before a user is known (login/signup), while
// authenticated pages get the user's saved primaryColor once it loads.
// MUI derives text.secondary, divider color, icon default color (action.active),
// and hover/disabled states from palette.mode - without this, those tokens stay
// stuck on light-mode defaults (near-black, low contrast) even when the page
// background itself has gone dark.
const DynamicThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { primaryColor, defaultTextColor, pageBackgroundColor, isDark } = useColorScheme();
  const dynamicTheme = useMemo(() => createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: primaryColor },
      secondary: { main: '#6c757d' },
      text: { primary: defaultTextColor },
      background: { paper: pageBackgroundColor },
    },
  }), [primaryColor, defaultTextColor, pageBackgroundColor, isDark]);
  return <ThemeProvider theme={dynamicTheme}>{children}</ThemeProvider>;
};

const AppWithSidebar: React.FC = () => {
  const [compressed, setCompressed] = useState(() => window.innerWidth < 1200);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Same 1200px threshold the initial state above already used, just made
  // reactive - without this the sidebar only ever picked its width once, at
  // mount, and stayed there even if the window was later resized past 1200px
  // in either direction (e.g. a laptop docked/undocked from an external
  // monitor, or just resizing the browser window).
  useEffect(() => {
    const handleResize = () => setCompressed(window.innerWidth < 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const hideSidebarRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/admin', '/admin/forgot-password', '/lab', '/terms-and-conditions'];
  const isViewerRoute = location.pathname.startsWith('/view/');

  return (
    <ThemeProvider theme={theme}>
      <AuthGuard>
      <UserProvider>
      <LogoutProvider>
      <ColorSchemeProvider>
      <DynamicThemeProvider>
      <PermissionsProvider>
      <FeaturesProvider>
      <SettingsProvider>
      <BankProvider>
      <NotificationProvider>
      <ProvisionAgingWatcher />
      <IdleTimeout />
      <PlanRequiredGate />
      <LocationProvider>
      <SidebarMobileProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SidebarContext.Provider value={{ compressed, setCompressed }}>
            <div style={{ display: 'flex' }}>
              {!hideSidebarRoutes.includes(location.pathname) && !isMobile && !isViewerRoute && <Sidebar />}
              <div
                style={{
                  flex: 1,
                  // Without this, a flex item defaults to min-width:auto, so
                  // any page content wide enough (an un-wrapped row of stat
                  // blocks, a long word) forces this whole content area -
                  // and the page inside it - past the viewport width instead
                  // of shrinking/wrapping to fit it. Only matters on narrow
                  // (mobile) widths; desktop always has room to spare.
                  minWidth: 0,
                  marginLeft: hideSidebarRoutes.includes(location.pathname) || isMobile || isViewerRoute
                    ? 0
                    : (compressed ? SIDEBAR_WIDTH_COMPRESSED : SIDEBAR_WIDTH),
                  transition: 'margin-left 0.2s',
                }}
              >
                <ErrorBoundary key={location.pathname}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/google/callback" element={<GoogleCallback />} />
                  <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
                  <Route path="/transaction" index element={<PermissionGuard resource="transactions"><TransactionsVirtual /></PermissionGuard>} />
                  <Route path="/linked-imports" element={<PermissionGuard resource="imports"><LinkedImports /></PermissionGuard>} />
                  <Route path="/provision" element={<PermissionGuard resource="provisions"><Provisions /></PermissionGuard>} />
                  <Route path="/gl" element={<PermissionGuard resource="categories"><CategoriesPage /></PermissionGuard>} />
                  <Route path="/invoices" element={<PermissionGuard resource="invoices"><Invoice /></PermissionGuard>} />
                  <Route path="/clients" element={<PermissionGuard resource="clients"><Client /></PermissionGuard>} />
                  <Route path="/tax-definitions" element={<PermissionGuard resource="tax_definitions"><TaxDefinitions /></PermissionGuard>} />
                  <Route path="/staff" element={<PermissionGuard resource="staff"><Staff /></PermissionGuard>} />
                  <Route path="/summary/assumptions" element={<PermissionGuard resource="summaries_assumptions"><Assumptions /></PermissionGuard>} />
                  <Route path="/summary/forecast-vs-actual" element={<PermissionGuard resource="summaries_forecast_vs_actual"><BudgetActual /></PermissionGuard>} />
                  <Route path="/summary/forecasts" element={<PermissionGuard resource="summaries_forecasts"><Forecast /></PermissionGuard>} />
                  <Route path="/summary/profit-loss" element={<PermissionGuard resource="summaries_profit_loss"><ProfitLoss /></PermissionGuard>} />
                  <Route path="/summary/gst" element={<PermissionGuard resource="summaries_gst"><GST /></PermissionGuard>} />
                  <Route path="/settings/company" element={<PermissionGuard resource="settings_company"><AccountSettings /></PermissionGuard>} />
                  <Route path="/settings/profile" element={<ProfileSettings />} />
                  <Route path="/settings/bank-accounts" element={<PermissionGuard resource="bank_accounts"><BankAccounts /></PermissionGuard>} />
                  <Route path="/settings/security" element={<PermissionGuard resource="settings_security"><Security /></PermissionGuard>} />
                  <Route path="/documents" element={<PermissionGuard resource="documents"><Documents /></PermissionGuard>} />
                  <Route path="/settings/staff-payment" element={<PermissionGuard resource="staff_payment"><Contributions /></PermissionGuard>} />
                  <Route path="/settings/invoice" element={<PermissionGuard resource="settings_invoice"><InvoiceSettings /></PermissionGuard>} />
                  <Route path="/settings/provisions" element={<PermissionGuard resource="settings_provisions"><ProvisionSettings /></PermissionGuard>} />
                  <Route path="/settings/imports" element={<PermissionGuard resource="imports"><Imports /></PermissionGuard>} />
                  <Route path="/settings/integrations" element={<PermissionGuard resource="integrations"><Integrations /></PermissionGuard>} />
                  <Route path="/color-scheme" element={<ColorSchemeSettings />} />
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/forgot-password" element={<AdminLogin />} />
                  <Route path="/admin/company" element={<Company />} />
                  <Route path="/admin/bank" element={<Bank />} />
                  <Route path="/admin/country" element={<Country />} />
                  <Route path="/admin/category-provider" element={<CategoryProviderPage />} />
                  <Route path="/admin/invoice-provider" element={<InvoiceProviderPage />} />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                  <Route path="/admin/activity-logs" element={<ActivityLogs />} />
                  {/* Merged into Activity Logs as its Auto-Provisions preset. */}
                  <Route path="/admin/activity-logs/auto-provisions" element={<Navigate to="/admin/activity-logs?preset=auto_provision" replace />} />
                  <Route path="/admin/impersonate" element={<AdminImpersonate />} />
                  <Route path="/admin/feature-flags" element={<FeatureFlagsAdmin />} />
                  <Route path="/admin/subscription-tiers" element={<SubscriptionTiersAdmin />} />
                  <Route path="/admin/subscription-addons" element={<SubscriptionAddonsAdmin />} />
                  <Route path="/admin/subscription-reimbursements" element={<SubscriptionReimbursementsAdmin />} />
                  <Route path="/admin/stripe-settings" element={<StripeSettingsAdmin />} />
                  <Route path="/view/:slug" element={<DocumentViewer />} />
                  <Route path="/lab" element={<Lab />} />
                  <Route path="/secret-options" element={<DevOptions />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                </Routes>
                </ErrorBoundary>
              </div>
            </div>
          </SidebarContext.Provider>
        </LocalizationProvider>
      </SidebarMobileProvider>
      </LocationProvider>
      </NotificationProvider>
      </BankProvider>
      </SettingsProvider>
      </FeaturesProvider>
      </PermissionsProvider>
      </DynamicThemeProvider>
      </ColorSchemeProvider>
      </LogoutProvider>
      </UserProvider>
      </AuthGuard>
    </ThemeProvider>
  );
};

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AppWithSidebar />
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();
